import styled, { css } from 'styled-components';
import { Link } from 'react-router-dom';

export const VerReservasHeaderButton = styled.button<{ $compact?: boolean }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.375rem;
    min-height: 2.75rem;
    min-width: ${p => (p.$compact ? '2.75rem' : 'auto')};
    padding: ${p => (p.$compact ? '0.25rem' : '0.25rem 0.5rem')};
    margin: 0;
    border: none;
    background: transparent;
    cursor: pointer;
    color: ${({ theme }) => theme.colors['neutral-100']};
    font-size: 1rem;
    font-weight: 500;
    line-height: 1.5rem;
    border-radius: 0.5rem;
    white-space: nowrap;

    &:hover {
        color: #ffffff;
        text-decoration: underline;
        text-underline-offset: 4px;
    }

    &:focus-visible {
        outline: 2px solid #ffcf00;
        outline-offset: 2px;
    }
`;

export const DrawerHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    margin-bottom: 1rem;
    padding-bottom: 0.75rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.2);

    h2 {
        margin: 0;
        font-size: 1.125rem;
        font-weight: 600;
        color: #f4f4f5;
    }

    button {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 2.75rem;
        height: 2.75rem;
        border: none;
        border-radius: 0.5rem;
        background: rgba(255, 255, 255, 0.1);
        color: #f4f4f5;
        cursor: pointer;

        &:focus-visible {
            outline: 2px solid #ffcf00;
            outline-offset: 2px;
        }

        &:hover {
            background: rgba(255, 255, 255, 0.18);
        }
    }
`;

export const PanelScroll = styled.div`
    flex: 1;
    overflow-y: auto;
    min-height: 0;
    padding-right: 0.25rem;
`;

export const ReservationCard = styled.article<{ $surface?: 'light' | 'dark' }>`
    padding: 1rem 1.125rem;
    margin-bottom: 0.75rem;
    border-radius: 0.75rem;

    ${p =>
        p.$surface === 'light'
            ? css`
                  background: #fafafa;
                  border: 1px solid ${({ theme }) => theme.colors['neutral-200']};
                  color: ${({ theme }) => theme.colors['neutral-900']};

                  h3 {
                      color: ${({ theme }) => theme.colors['neutral-900']};
                  }

                  dt {
                      color: ${({ theme }) => theme.colors['neutral-600']};
                  }

                  dd {
                      color: ${({ theme }) => theme.colors['neutral-900']};
                  }
              `
            : css`
                  background: rgba(255, 255, 255, 0.08);
                  border: 1px solid rgba(255, 255, 255, 0.12);
                  color: #f4f4f5;

                  h3 {
                      color: #fafafa;
                  }

                  dt {
                      color: #a1a1aa;
                  }

                  dd {
                      color: #f4f4f5;
                  }
              `}

    h3 {
        margin: 0 0 0.5rem;
        font-size: 1rem;
        font-weight: 600;
    }

    dl {
        margin: 0;
        display: grid;
        gap: 0.35rem 1rem;
        font-size: 0.875rem;
        line-height: 1.35rem;
    }

    dt {
        margin: 0;
        font-weight: 500;
    }

    dd {
        margin: 0;
        font-weight: 500;
    }
`;

export const ReservationSummaryToggleWrap = styled.div<{ $surface?: 'light' | 'dark' }>`
    margin-top: 0.875rem;
    padding-top: 0.5rem;
    border-top: 1px solid
        ${p =>
            p.$surface === 'light'
                ? p.theme.colors['neutral-200']
                : 'rgba(255, 255, 255, 0.14)'};
`;

export const ReservationSummaryToggle = styled.button<{ $surface?: 'light' | 'dark' }>`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    min-height: 2.75rem;
    padding: 0.5rem 0.375rem;
    margin: 0;
    border: none;
    border-radius: 0.5rem;
    background: transparent;
    cursor: pointer;
    text-align: left;
    font-size: 0.875rem;
    font-weight: 600;
    line-height: 1.35rem;
    color: ${p =>
        p.$surface === 'light' ? p.theme.colors['neutral-900'] : '#fafafa'};
    transition: background-color 0.2s ease;

    &:hover {
        background: ${p =>
            p.$surface === 'light' ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.06)'};
    }

    &:focus-visible {
        outline: 2px solid #008cff;
        outline-offset: 2px;
    }

    svg {
        flex-shrink: 0;
        color: ${p =>
            p.$surface === 'light' ? p.theme.colors['neutral-600'] : '#a1a1aa'};
        transition: transform 0.2s ease;
    }

    &[aria-expanded='true'] svg {
        transform: rotate(90deg);
    }

    @media (prefers-reduced-motion: reduce) {
        transition: none;

        svg {
            transition: none;
        }
    }
`;

export const ReservationWavesSection = styled.div<{
    $surface?: 'light' | 'dark';
    $inset?: boolean;
}>`
    margin-top: ${p => (p.$inset ? '0.25rem' : '0.875rem')};
    padding-top: ${p => (p.$inset ? '0.5rem' : '0.75rem')};
    border-top: ${p =>
        p.$inset
            ? 'none'
            : `1px solid ${
                  p.$surface === 'light'
                      ? p.theme.colors['neutral-200']
                      : 'rgba(255, 255, 255, 0.14)'
              }`};

    dl {
        margin: 0;
        display: grid;
        gap: 0.3rem 0.75rem;
        font-size: 0.8125rem;
        line-height: 1.35rem;
    }

    dt {
        margin: 0;
        font-weight: 500;
    }

    dd {
        margin: 0;
        font-weight: 500;
    }
`;

export const ReservationWavesAllScheduled = styled.p<{ $surface?: 'light' | 'dark' }>`
    margin: 0.75rem 0 0;
    font-size: 0.8125rem;
    line-height: 1.4;
    font-weight: 500;
    color: ${p =>
        p.$surface === 'light' ? p.theme.colors['neutral-600'] : '#d4d4d8'};
`;

/** Navega para `/reserva?token=…` (também aceito: `reservationToken`), alinhado a `ReservationValidation`. */
export const ReservationAccessLink = styled(Link)`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-top: 1rem;
    min-height: 2.75rem;
    padding: 0.5rem 1rem;
    border-radius: 0.5rem;
    font-weight: 600;
    font-size: 0.9375rem;
    line-height: 1.25rem;
    text-decoration: none;
    cursor: pointer;
    background: #ffcf00;
    color: #09090b;
    border: 1px solid rgba(0, 0, 0, 0.06);

    &:hover {
        background: #ffd724;
        color: #09090b;
    }

    &:focus-visible {
        outline: 2px solid #008cff;
        outline-offset: 2px;
    }
`;

export const CenteredState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    padding: 2rem 1rem;
    text-align: center;
    color: ${({ theme }) => theme.colors['neutral-900']};
    font-size: 1rem;
    font-weight: 500;
    line-height: 1.5rem;

    &.inverse {
        color: #e4e4e7;
    }
`;

export const ModalRetryButton = styled.button`
    margin-top: 0.5rem;
    padding: 0.5rem 1rem;
    border-radius: 0.5rem;
    border: 1px solid ${({ theme }) => theme.colors['neutral-300']};
    background: #ffffff;
    color: ${({ theme }) => theme.colors['neutral-900']};
    font-weight: 600;
    font-size: 0.9375rem;
    cursor: pointer;

    &:focus-visible {
        outline: 2px solid #008cff;
        outline-offset: 2px;
    }

    &:hover {
        background: ${({ theme }) => theme.colors['neutral-100']};
    }
`;

export const DrawerRetryButton = styled.button`
    margin-top: 0.5rem;
    padding: 0.5rem 1rem;
    border-radius: 0.5rem;
    border: 1px solid rgba(255, 255, 255, 0.35);
    background: rgba(255, 255, 255, 0.12);
    color: #fafafa;
    font-weight: 600;
    font-size: 0.9375rem;
    cursor: pointer;

    &:focus-visible {
        outline: 2px solid #ffcf00;
        outline-offset: 2px;
    }

    &:hover {
        background: rgba(255, 255, 255, 0.2);
    }
`;

export const Spinner = styled.div`
    width: 2.25rem;
    height: 2.25rem;
    border: 3px solid rgba(0, 0, 0, 0.12);
    border-top-color: ${({ theme }) => theme.colors['neutral-900']};
    border-radius: 50%;
    animation: ownerResSpin 0.7s linear infinite;

    &.inverse {
        border-color: rgba(255, 255, 255, 0.2);
        border-top-color: #fafafa;
    }

    @keyframes ownerResSpin {
        to {
            transform: rotate(360deg);
        }
    }

    @media (prefers-reduced-motion: reduce) {
        animation: none;
        border-top-color: transparent;
    }
`;
